/**
 * ChangePasswordModal — re-authenticates with the current password and then
 * sets a new one via Firebase Auth. Opened from the user menu in the
 * top-right avatar dropdown. Email/password accounts only — Google-signed-in
 * users see a static notice instead of the form (their password is managed
 * by Google).
 *
 * Firebase requires a fresh credential before sensitive ops; we surface the
 * `auth/wrong-password` and `auth/requires-recent-login` errors as inline
 * messages so the user knows whether to retry the current password or to
 * sign out and back in.
 *
 * The shared `<PasswordField>` primitive is exported and reused by the
 * other personal-account modals (change email, delete account) so the
 * styling stays consistent.
 */
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { FirebaseError } from 'firebase/app'
import { Modal } from '@/shared/ui/modal'
import { Button } from '@/shared/ui'
import { logger } from '@/core/logger'
import { authRepository } from '../api/auth-repository'
import { ChangePasswordSchema, type ChangePasswordValues } from '../schemas'

const FRIENDLY_ERRORS: Record<string, string> = {
  'auth/wrong-password': 'La contraseña actual no es correcta.',
  'auth/invalid-credential': 'La contraseña actual no es correcta.',
  'auth/weak-password': 'La nueva contraseña es demasiado débil.',
  'auth/requires-recent-login':
    'Por seguridad, vuelve a iniciar sesión y prueba de nuevo.',
  'auth/too-many-requests':
    'Demasiados intentos. Inténtalo en unos minutos.',
  'auth/network-request-failed': 'Sin conexión. Comprueba tu red.',
}

export interface ChangePasswordModalProps {
  /** True when the signed-in user authenticates via password (vs Google). */
  hasPasswordProvider: boolean
  onClose: () => void
  /** Called after a successful password change so the host can show a toast etc. */
  onSuccess?: () => void
}

export function ChangePasswordModal({
  hasPasswordProvider,
  onClose,
  onSuccess,
}: ChangePasswordModalProps) {
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ChangePasswordValues>({
    resolver: zodResolver(ChangePasswordSchema),
    defaultValues: { currentPassword: '', newPassword: '', confirmNewPassword: '' },
    mode: 'onBlur',
  })

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null)
    try {
      await authRepository.changePassword(values.currentPassword, values.newPassword)
      setDone(true)
      onSuccess?.()
    } catch (e) {
      const code = (e as FirebaseError)?.code ?? 'unknown'
      logger.w('change password failed', code)
      setSubmitError(
        FRIENDLY_ERRORS[code] ??
          'No pudimos cambiar la contraseña. Inténtalo de nuevo.',
      )
    }
  })

  if (!hasPasswordProvider) {
    return (
      <Modal
        title="Contraseña gestionada por Google"
        size="sm"
        onClose={onClose}
        foot={
          <Button variant="primary" size="sm" onClick={onClose} data-autofocus>
            Entendido
          </Button>
        }
      >
        <p
          style={{
            margin: 0,
            fontSize: 'var(--fs-md)',
            lineHeight: 'var(--lh-relaxed)',
            color: 'var(--fg-muted)',
          }}
        >
          Iniciaste sesión con Google. Para cambiar la contraseña, hazlo desde
          tu cuenta de Google. uPadelMatch no almacena ni puede modificar esa
          contraseña.
        </p>
      </Modal>
    )
  }

  if (done) {
    return (
      <Modal
        title="Contraseña actualizada"
        size="sm"
        onClose={onClose}
        foot={
          <Button variant="primary" size="sm" onClick={onClose} data-autofocus>
            Cerrar
          </Button>
        }
      >
        <p
          role="status"
          aria-live="polite"
          style={{
            margin: 0,
            fontSize: 'var(--fs-md)',
            lineHeight: 'var(--lh-relaxed)',
            color: 'var(--fg-muted)',
          }}
        >
          Usa la nueva contraseña la próxima vez que inicies sesión.
        </p>
      </Modal>
    )
  }

  return (
    <Modal
      title="Cambiar contraseña"
      size="sm"
      onClose={onClose}
      dirty={isDirty && !isSubmitting}
      foot={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={onSubmit}
            disabled={isSubmitting}
            loading={isSubmitting}
          >
            Cambiar
          </Button>
        </div>
      }
    >
      <form
        onSubmit={onSubmit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap)' }}
      >
        <PasswordField
          label="Contraseña actual"
          autoComplete="current-password"
          error={errors.currentPassword?.message}
          {...register('currentPassword')}
        />
        <PasswordField
          label="Nueva contraseña"
          autoComplete="new-password"
          error={errors.newPassword?.message}
          {...register('newPassword')}
        />
        <PasswordField
          label="Confirmar nueva contraseña"
          autoComplete="new-password"
          error={errors.confirmNewPassword?.message}
          {...register('confirmNewPassword')}
        />

        {submitError && (
          <div
            role="alert"
            style={{
              padding: '8px 10px',
              border: '2px solid var(--bad)',
              borderRadius: 6,
              color: 'var(--bad)',
              fontSize: 'var(--fs-md)',
              background: 'var(--panel-2)',
            }}
          >
            {submitError}
          </div>
        )}
      </form>
    </Modal>
  )
}

/**
 * Shared input field used by the personal-account modals (change-password,
 * change-email, delete-account). Exported so neighboring modals don't need
 * to redefine the styling. `type` defaults to `password` to match the most
 * common case; pass `type="email"` etc. when needed.
 */
export const PasswordField = function PasswordFieldImpl({
  label,
  error,
  type = 'password',
  ...rest
}: React.InputHTMLAttributes<HTMLInputElement> & {
  label: string
  error?: string | undefined
}) {
  const id = `pw-${label.replace(/\s+/g, '-')}`
  return (
    <label htmlFor={id} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--fg-muted)' }}>{label}</span>
      <input
        {...rest}
        id={id}
        type={type}
        spellCheck={false}
        aria-invalid={!!error}
        style={{
          padding: '8px 10px',
          border: `2px solid ${error ? 'var(--bad)' : 'var(--line)'}`,
          borderRadius: 6,
          background: 'var(--panel-2)',
          color: 'var(--fg)',
          fontSize: 'var(--fs-md)',
        }}
      />
      {error && (
        <span role="alert" style={{ fontSize: 'var(--fs-xs)', color: 'var(--bad)' }}>
          {error}
        </span>
      )}
    </label>
  )
}
